import { BayLevelEnum, ForeAftEnum } from "open-vessel-definition";
import { IBayPattern, getAllThePairedBays } from "@tedivo/tedivo-bay-grid-pure";
import {
  IValidationInput,
  IValidationOutput,
  IValidator,
  TValidatorSpecialScopes,
} from "../types";
import { createDictionary, pad3 } from "@tedivo/tedivo-pure-helpers";

import { IIsoBayPattern } from "open-vessel-definition/build/src/models/base/types/IPositionPatterns";

const name = "allBaysWith40sArePairedValidator";
const okTranslationKey = "validators:bays.allBaysWith40sArePaired.ok";
const errorTranslationKey = "validators:bays.allBaysWith40sArePaired.error";

const specialScopes: TValidatorSpecialScopes[] = ["exportStaf"];

/** Check if all bays with 40s are paired */
export const allBaysWith40sArePairedValidator: IValidator<IAllBaysWith40sArePairedOutput> =
{
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: allBaysWith40sArePairedValidatorFn,
  specialScopes,
};

function allBaysWith40sArePairedValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<IAllBaysWith40sArePairedOutput> {
  const baysData = json.baysData;
  const baysDataDict = createDictionary(
    baysData,
    (b) => `${b.isoBay}-${b.level}`,
  );

  const { pairedBays, unpairedBays } = getAllThePairedBays(baysData, true);
  const isValid = unpairedBays.length === 0;

  const validResultsSet = new Set(
    pairedBays.flatMap(({ base, paired, level }) => [
      `${base}-${level}`,
      `${paired}-${level}`,
    ]),
  );

  const invalidResultsSet = new Set(
    unpairedBays.map(({ base, level }) => `${base}-${level}`),
  );

  // If the pairing is different between ABOVE & BELOW it's not valid
  const validResultsIsoBays = pairedBays.reduce((acc, { base, paired }) => {
    acc.add(base);
    acc.add(paired);
    return acc;
  }, new Set<IBayPattern>());

  validResultsIsoBays.forEach((bay) => {
    const blAbove = baysDataDict[`${bay}-${BayLevelEnum.ABOVE}`];
    const blBelow = baysDataDict[`${bay}-${BayLevelEnum.BELOW}`];

    if (blAbove === undefined || blBelow === undefined) {
      return;
    }

    if (blAbove.pairedBay !== blBelow.pairedBay) {
      invalidResultsSet
        .add(`${bay}-${BayLevelEnum.ABOVE}`)
        .add(`${bay}-${BayLevelEnum.BELOW}`);

      if (validResultsSet.has(`${bay}-${BayLevelEnum.ABOVE}`)) {
        validResultsSet.delete(`${bay}-${BayLevelEnum.ABOVE}`);
      }

      if (validResultsSet.has(`${bay}-${BayLevelEnum.BELOW}`)) {
        validResultsSet.delete(`${bay}-${BayLevelEnum.BELOW}`);
      }
    }
  });

  // If a bay is paired to another bay that is not paired to it, both are invalid
  invalidResultsSet.forEach((r) => {
    const [isoBay, level] = r.split("-") as [IIsoBayPattern, BayLevelEnum];
    const bl = baysDataDict[`${isoBay}-${level}`];

    if (bl?.pairedBay) {
      const targetBayNumber = pad3(Number(bl.isoBay) + (bl.pairedBay === ForeAftEnum.FWD ? -2 : 2));
      const targetBay = baysDataDict[`${targetBayNumber}-${level}`];
      if (targetBay && targetBay.pairedBay !== bl.pairedBay) {
        invalidResultsSet.add(`${targetBayNumber}-${level}`);
      }
    }
  });

  return {
    name,
    specialScopes,
    isValid,
    messageTranslationKey: isValid ? okTranslationKey : errorTranslationKey,
    result: {
      validResults: new Array(...validResultsSet).map((r) => {
        const [isoBay, level] = r.split("-") as [IIsoBayPattern, BayLevelEnum];
        return {
          bay: isoBay,
          level: Number(level) as BayLevelEnum,
        };
      }),
      invalidResults: new Array(...invalidResultsSet).map((r) => {
        const [isoBay, level] = r.split("-") as [IIsoBayPattern, BayLevelEnum];
        return {
          bay: isoBay,
          level: Number(level) as BayLevelEnum,
        };
      }),
    },
  };
}

export interface IAllBaysWith40sArePairedOutput {
  validResults: IAllBaysWith40sArePairedSingleResult[];
  invalidResults: IAllBaysWith40sArePairedSingleResult[];
}

interface IAllBaysWith40sArePairedSingleResult {
  bay: IBayPattern;
  level: BayLevelEnum;
}
